// Iter62p / Iter62q — Hugo vs Rayan personal-objectives competition widget.
// Dark Premium iOS glass card. Period selector: semaine / mois / trimestre.
// Renders per-founder assiduité + mini bar chart + short textual bilan.
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Trophy } from './icons';

function pct(n) {
  return `${Math.round((n || 0) * 100)}%`;
}

const PERIODS = [
  { key: 'week', label: 'Semaine' },
  { key: 'month', label: 'Mois' },
  { key: 'quarter', label: 'Trimestre' },
];

export default function FoundersLeaderboard() {
  const [period, setPeriod] = useState('week');
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    let alive = true;
    setData(null);
    api.get(`/objectives/leaderboard?period=${period}&weeks=8`)
      .then((r) => { if (alive) setData(r.data); })
      .catch(() => { if (alive) setErr(true); });
    return () => { alive = false; };
  }, [period]);

  if (err || !data) return null;

  const [a, b] = data.founders;
  let leader = null;
  if (a.rate > b.rate) leader = a.id;
  else if (b.rate > a.rate) leader = b.id;
  else if (a.done > b.done) leader = a.id;
  else if (b.done > a.done) leader = b.id;

  const buckets = data.buckets || [];
  const maxBucket = Math.max(
    1,
    ...buckets.map((w) => Math.max(
      w.hugo.done + w.hugo.failed + w.hugo.pending,
      w.rayan.done + w.rayan.failed + w.rayan.pending,
    )),
  );

  // Short textual bilan for the current period
  const bilan = buildBilan(data.founders, period, leader);

  return (
    <div
      data-testid="founders-leaderboard"
      style={{
        position: 'relative',
        marginBottom: 18,
        borderRadius: 18,
        padding: '18px 20px 16px',
        overflow: 'hidden',
        background:
          'radial-gradient(120% 100% at 100% 0%, rgba(167,139,250,0.14) 0%, rgba(167,139,250,0) 55%),' +
          ' radial-gradient(120% 100% at 0% 100%, rgba(91,190,252,0.14) 0%, rgba(91,190,252,0) 55%),' +
          ' linear-gradient(160deg, rgba(28,38,80,0.85) 0%, rgba(16,24,58,0.94) 100%)',
        backdropFilter: 'blur(20px) saturate(160%)',
        WebkitBackdropFilter: 'blur(20px) saturate(160%)',
        border: '1px solid rgba(127,173,255,0.22)',
        boxShadow: '0 16px 34px -16px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.06)',
      }}
    >
      {/* Header + Period selector */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <div
          style={{
            width: 34, height: 34, borderRadius: 10,
            background: 'linear-gradient(135deg, rgba(245,166,35,0.30), rgba(245,166,35,0.10))',
            border: '1px solid rgba(245,166,35,0.45)',
            display: 'grid', placeItems: 'center',
            boxShadow: '0 6px 16px -6px rgba(245,166,35,0.45), inset 0 1px 0 rgba(255,255,255,0.10)',
          }}
        >
          <Trophy size={16} color="#F5A623" strokeWidth={1.9} />
        </div>
        <div style={{ flex: 1, minWidth: 160 }}>
          <div style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 14, color: 'var(--t1)', letterSpacing: '-0.01em' }}>
            Duel des Founders
          </div>
          <div style={{ fontFamily: 'Lexend', fontSize: 11.5, color: 'var(--t3)' }}>
            Assiduité sur les objectifs personnels
          </div>
        </div>

        {/* Segmented period switcher */}
        <div
          data-testid="leaderboard-period-switch"
          style={{
            display: 'inline-flex', gap: 2, padding: 3, borderRadius: 10,
            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)',
          }}
        >
          {PERIODS.map((p) => (
            <button
              key={p.key}
              data-testid={`leaderboard-period-${p.key}`}
              onClick={() => setPeriod(p.key)}
              style={{
                padding: '6px 12px', borderRadius: 8,
                background: period === p.key ? 'linear-gradient(135deg, rgba(127,173,255,0.22), rgba(62,123,250,0.10))' : 'transparent',
                border: `1px solid ${period === p.key ? 'rgba(127,173,255,0.42)' : 'transparent'}`,
                color: period === p.key ? '#B8D4FF' : 'var(--t3)',
                fontFamily: 'Sora', fontWeight: 700, fontSize: 11.5,
                cursor: 'pointer',
                boxShadow: period === p.key ? 'inset 0 1px 0 rgba(255,255,255,0.08)' : 'none',
              }}
            >{p.label}</button>
          ))}
        </div>
      </div>

      {/* Two founder columns */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        {data.founders.map((f) => (
          <FounderCol key={f.id} f={f} isLeader={leader === f.id} />
        ))}
      </div>

      {/* Bilan textuel */}
      {bilan && (
        <div
          data-testid="leaderboard-bilan"
          style={{
            marginTop: 14, padding: '10px 12px', borderRadius: 12,
            background: 'linear-gradient(135deg, rgba(245,166,35,0.08), rgba(245,166,35,0.02))',
            border: '1px solid rgba(245,166,35,0.20)',
            fontFamily: 'Lexend', fontSize: 12, color: 'var(--t2)', lineHeight: 1.5,
          }}
        >
          <span style={{ fontFamily: 'Sora', fontWeight: 800, color: '#F5A623', marginRight: 6 }}>Bilan</span>
          {bilan}
        </div>
      )}

      {/* Bar chart */}
      <div style={{ marginTop: 14 }}>
        <div
          style={{
            fontFamily: 'Sora', fontWeight: 700, fontSize: 10,
            letterSpacing: '0.06em', textTransform: 'uppercase',
            color: 'var(--t3)', marginBottom: 8,
          }}
        >
          {period === 'week' ? 'Semaines' : period === 'month' ? 'Mois' : 'Trimestres'}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, height: 64 }}>
          {buckets.map((w) => {
            const hh = ((w.hugo.done + w.hugo.failed + w.hugo.pending) / maxBucket) * 100;
            const rh = ((w.rayan.done + w.rayan.failed + w.rayan.pending) / maxBucket) * 100;
            const hDone = w.hugo.done;
            const hTot = w.hugo.done + w.hugo.failed + w.hugo.pending;
            const rDone = w.rayan.done;
            const rTot = w.rayan.done + w.rayan.failed + w.rayan.pending;
            return (
              <div
                key={w.key}
                title={`${w.label} — Hugo ${hDone}/${hTot} · Rayan ${rDone}/${rTot}`}
                style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 4 }}
              >
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: '100%' }}>
                  <MiniBar heightPct={hh} donePct={hTot ? (hDone / hTot) * 100 : 0} color="#5BBEFC" />
                  <MiniBar heightPct={rh} donePct={rTot ? (rDone / rTot) * 100 : 0} color="#2EA3F2" />
                </div>
                <div style={{ fontFamily: 'Sora', fontWeight: 700, fontSize: 9, color: 'var(--t3)', textAlign: 'center', opacity: 0.7 }}>
                  {w.label}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function buildBilan(founders, period, leaderId) {
  const [a, b] = founders;
  if (!a || !b) return '';
  const label = period === 'week' ? 'cette période (semaines)' : period === 'month' ? 'ces derniers mois' : 'ces derniers trimestres';
  if (!leaderId) {
    if (a.total === 0 && b.total === 0) return `Aucun objectif personnel enregistré ${label}.`;
    return `Match nul entre ${a.name.split(' ')[0]} et ${b.name.split(' ')[0]} — même assiduité (${pct(a.rate)}).`;
  }
  const leader = founders.find((f) => f.id === leaderId);
  const other = founders.find((f) => f.id !== leaderId);
  const gap = Math.round(((leader.rate - other.rate) * 100)) || 0;
  const gapStr = gap > 0 ? `+${gap} pts d'avance` : `écart minime`;
  return `${leader.name.split(' ')[0]} mène ${label} avec ${pct(leader.rate)} d'assiduité (${leader.done} ✓ / ${leader.failed} ✗), soit ${gapStr} sur ${other.name.split(' ')[0]} (${pct(other.rate)}).`;
}

function FounderCol({ f, isLeader }) {
  return (
    <div
      data-testid={`leaderboard-founder-${f.id}`}
      style={{
        position: 'relative',
        padding: '12px 12px 10px',
        borderRadius: 14,
        background: isLeader
          ? `linear-gradient(160deg, ${f.color}26, ${f.color}0A)`
          : 'linear-gradient(160deg, rgba(255,255,255,0.04), rgba(255,255,255,0.02))',
        border: `1px solid ${isLeader ? `${f.color}55` : 'rgba(255,255,255,0.08)'}`,
        boxShadow: isLeader
          ? `0 4px 16px -6px ${f.color}55, inset 0 1px 0 rgba(255,255,255,0.06)`
          : 'inset 0 1px 0 rgba(255,255,255,0.03)',
      }}
    >
      {isLeader && (
        <div
          style={{
            position: 'absolute', top: -8, right: 10,
            padding: '2px 8px', borderRadius: 999,
            background: `linear-gradient(135deg, ${f.color}, #F5A623)`,
            color: '#0A1428', fontFamily: 'Sora', fontWeight: 800,
            fontSize: 9, letterSpacing: '0.06em', textTransform: 'uppercase',
            boxShadow: '0 4px 10px -4px rgba(245,166,35,0.55)',
          }}
        >
          Leader
        </div>
      )}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <div
          style={{
            width: 30, height: 30, borderRadius: '50%',
            background: `${f.color}22`,
            border: `1.5px solid ${f.color}66`,
            color: f.color, display: 'grid', placeItems: 'center',
            fontFamily: 'Sora', fontWeight: 800, fontSize: 13,
          }}
        >
          {(f.name || '?').charAt(0)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: 'Sora', fontWeight: 700, fontSize: 12.5,
              color: 'var(--t1)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}
          >
            {f.name}
          </div>
          <div style={{ fontFamily: 'Lexend', fontSize: 10.5, color: 'var(--t3)' }}>
            {f.total} objectif{f.total > 1 ? 's' : ''}
          </div>
        </div>
      </div>

      <div style={{ position: 'relative', height: 6, borderRadius: 999, background: 'rgba(255,255,255,0.06)', overflow: 'hidden' }}>
        <div
          style={{
            position: 'absolute', inset: 0,
            width: `${(f.rate || 0) * 100}%`,
            background: `linear-gradient(90deg, ${f.color}, ${f.color}CC)`,
            boxShadow: `0 0 12px ${f.color}66`,
            borderRadius: 999,
            transition: 'width 0.4s ease',
          }}
        />
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 8 }}>
        <span style={{ fontFamily: 'Sora', fontWeight: 800, fontSize: 22, color: 'var(--t1)', letterSpacing: '-0.02em' }}>
          {pct(f.rate)}
        </span>
        <span style={{ fontFamily: 'Lexend', fontSize: 10.5, color: 'var(--t3)' }}>assiduité</span>
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 6, fontFamily: 'Sora', fontWeight: 700, fontSize: 10.5 }}>
        <span style={{ color: '#34D399' }}>✓ {f.done}</span>
        <span style={{ color: '#ef4444' }}>✗ {f.failed}</span>
        <span style={{ color: 'var(--t3)' }}>⏳ {f.pending}</span>
      </div>
    </div>
  );
}

function MiniBar({ heightPct, donePct, color }) {
  const h = Math.max(2, heightPct);
  return (
    <div
      style={{
        flex: 1,
        position: 'relative',
        height: `${h}%`,
        minHeight: 2,
        borderRadius: 3,
        background: 'rgba(255,255,255,0.06)',
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          position: 'absolute', left: 0, right: 0, bottom: 0,
          height: `${donePct}%`,
          background: `linear-gradient(180deg, ${color}, ${color}AA)`,
          boxShadow: `0 0 8px ${color}55`,
          borderRadius: 3,
        }}
      />
    </div>
  );
}
